import { useMemo, useState } from "react";
import { BarChart3, Receipt, ArrowDownCircle, ArrowUpCircle, HandCoins } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AdminTitle } from "@/admin/AdminLayout";
import { GradientStat } from "@/components/Colorful";
import { useApi } from "@/lib/api";
import { rp, fmtDate, POSITIONS } from "@/lib/format";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const POS_TONE = { Teller: "bg-blue-100 text-blue-700", "Kabag Kredit": "bg-orange-100 text-orange-700", "Kepala TP": "bg-violet-100 text-violet-700", Manager: "bg-rose-100 text-rose-700" };

export default function AdminReports() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [pos, setPos] = useState("all");
  const days = from && to ? (new Date(to) - new Date(from)) / 86400000 : -1;
  const validRange = days >= 0 && days <= 366;
  const { data } = useApi(validRange ? `/admin/reports/staff?date_from=${from}&date_to=${to}` : null);
  const rows = useMemo(() => (validRange ? data || [] : []).filter((r) => pos === "all" || r.position === pos), [data, pos, validRange]);
  const sum = (k) => rows.reduce((s, r) => s + r[k], 0);

  return (
    <div>
      <AdminTitle icon={BarChart3} title="Laporan Kinerja Petugas" sub="Jumlah dan nilai transaksi yang diproses setiap petugas per hari">
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="text-xs font-semibold text-slate-600">Dari</label><Input type="date" data-testid="report-date-from" className="bg-white h-10 mt-1" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Sampai</label><Input type="date" data-testid="report-date-to" className="bg-white h-10 mt-1" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Jabatan</label>
            <select data-testid="report-position" value={pos} onChange={(e) => setPos(e.target.value)} className="block mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="all">Semua jabatan</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </AdminTitle>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        <GradientStat tone="blue" icon={Receipt} label="Jumlah Transaksi" value={sum("tx_count")} testid="report-total-count" />
        <GradientStat tone="green" icon={ArrowDownCircle} label="Total Setoran / Masuk" value={rp(sum("total_in"))} testid="report-total-in" />
        <GradientStat tone="red" icon={ArrowUpCircle} label="Total Penarikan / Keluar" value={rp(sum("total_out"))} testid="report-total-out" />
        <GradientStat tone="orange" icon={HandCoins} label="Keputusan Pinjaman" value={sum("loan_decisions")} testid="report-total-loans" />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="bg-gradient-to-r from-slate-50 to-blue-50 text-left text-xs font-mono uppercase tracking-wide text-slate-600">
            <tr><th className="p-3">Tanggal</th><th className="p-3">Petugas</th><th className="p-3">Jabatan</th><th className="p-3 text-right">Jml Transaksi</th><th className="p-3 text-right">Masuk</th><th className="p-3 text-right">Keluar</th><th className="p-3 text-right">Total Nilai</th><th className="p-3 text-right">Keputusan Pinjaman</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500" data-testid="report-empty">{validRange ? "Belum ada transaksi oleh petugas pada periode ini." : "Pilih rentang tanggal yang valid (maksimal 1 tahun)."}</td></tr>}
            {rows.map((r) => (
              <tr key={`${r.date}-${r.user_id}`} className="border-t border-slate-100 hover:bg-blue-50/40" data-testid={`report-row-${r.date}-${r.user_id}`}>
                <td className="p-3 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="p-3 font-semibold">{r.name}</td>
                <td className="p-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${POS_TONE[r.position] || "bg-slate-100 text-slate-600"}`}>{r.position}</span></td>
                <td className="p-3 text-right font-semibold">{r.tx_count}</td>
                <td className="p-3 text-right text-emerald-600 whitespace-nowrap">{rp(r.total_in)}</td>
                <td className="p-3 text-right text-red-600 whitespace-nowrap">{rp(r.total_out)}</td>
                <td className="p-3 text-right font-bold whitespace-nowrap">{rp(r.total_value)}</td>
                <td className="p-3 text-right">{r.loan_decisions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
